import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Plus,
  DollarSign,
  AlertTriangle,
  ArrowLeft,
  Clock,
  CheckCircle,
  Filter,
  Loader2,
  ChevronRight,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap, unwrapArray } from '../lib/api-envelope';
import { formatInstantDay, formatInstantTenantDay } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import { fmtCurrency } from '../lib/display-labels';
import SortTh, { sortCompare, makeToggleSort } from '../components/SortTh';

// --- Types ---
interface InvoiceDashboard {
  outstanding: number;
  overdueCount: number;
  paidThisMonth: number;
  draftsCount: number;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'void';
  totalAmount: number;
  dueDate: string;
  createdAt: string;
}

interface Customer {
  id: string;
  name: string;
}

// --- Status filter tabs ---
const STATUS_TABS: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'finalized', label: 'Saved' },
  { key: 'sent', label: 'Sent' },
  { key: 'paid', label: 'Paid' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'void', label: 'Void' },
];

// --- Status badge config ---
const STATUS_BADGE: Record<string, { label: string; textColor: string; bg: string; border: string; strikethrough?: boolean }> = {
  draft: { label: 'Draft', textColor: 'text-gray-400', bg: 'bg-gray-500/10', border: 'border-gray-500/30' },
  finalized: { label: 'Saved', textColor: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  sent: { label: 'Sent', textColor: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
  paid: { label: 'Paid', textColor: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  overdue: { label: 'Overdue', textColor: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  void: { label: 'Void', textColor: 'text-gray-500', bg: 'bg-gray-500/10', border: 'border-gray-500/30', strikethrough: true },
};

// --- Status dot colors (for desktop table) ---
const STATUS_DOT: Record<string, string> = {
  draft: 'bg-gray-400',
  finalized: 'bg-green-400',
  sent: 'bg-blue-400',
  paid: 'bg-green-400',
  overdue: 'bg-red-400',
  void: 'bg-gray-500',
};

// --- Money formatter ---
/** @see fmtCurrency — absence renders '—', not '$0.00'. */
const formatMoney = (amount: number | string | null | undefined) => fmtCurrency(amount);

// --- Format date for display ---
/** createdAt is an instant: the TENANT's day it fell on. Absent, unparseable
 *  or no known zone is an em dash. */
function formatDate(dateStr: string, timeZone: string | null): string {
  return formatInstantDay(dateStr, timeZone, { month: 'short', day: 'numeric', year: 'numeric' }, 'en-US') ?? '\u2014';
}

/**
 * dueDate is an INSTANT shown as a day (api#838) — the TENANT's day, not the
 * UTC prefix, which reads a day late for anything written after 18:00 MDT.
 * Absent is an em dash.
 */
function formatDay(dateStr: string | null | undefined, timeZone: string | null): string {
  return formatInstantTenantDay(dateStr, timeZone) ?? '\u2014';
}

/**
 * There is no demo data on this screen any more.
 *
 * A DEMO_INVOICES array of seven fabricated invoices, attributed to five
 * named oil and gas operators and totalling $191,076, was substituted
 * whenever the real list came back empty, and the Outstanding / Paid /
 * Overdue tiles were then derived from it. The route carries no vertical
 * guard, so every tenant with no invoices saw invented receivables presented
 * as their own: Dutch Casing Services, a live pilot, holds zero invoices, as
 * does Lone Star. The operator names are deliberately not repeated here --
 * they must not survive in the built bundle, source maps included.
 *
 * An empty list is now an empty state. See the two branches below -- a
 * request that FAILED and a tenant that genuinely has none are different
 * facts and must not render the same sentence.
 */

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function InvoicesScreen() {
  const timeZone = useTenantTimeZone();
  const navigate = useNavigate();
  const [status, setStatus] = useState('all');
  const [customerId, setCustomerId] = useState('');

  // Dashboard summary
  const { data: dashboardData, isLoading: dashboardLoading } = useQuery({
    queryKey: ['invoice-dashboard'],
    queryFn: async () => {
      try {
        const res = await api.invoices.dashboard();
        const d = unwrap<InvoiceDashboard>(res.data);
        if (d && typeof d.outstanding === 'number') return d;
        return null;
      } catch {
        return null;
      }
    },
    retry: false,
  });

  // Invoice list.
  //
  // No try/catch here on purpose. Swallowing the error into [] made a refused
  // or failed request render as "no invoices" -- a refusal reading as a fact
  // about the company. React Query surfaces it as isError instead, and the
  // list below has a separate branch for it.
  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    isError: invoicesError,
  } = useQuery({
    queryKey: ['invoices', status, customerId],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (status !== 'all') params.status = status;
      if (customerId) params.customerId = customerId;
      const res = await api.invoices.list(params);
      return unwrapArray<Invoice>(res.data);
    },
    retry: false,
  });

  // Customer list for filter dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      try {
        const res = await api.customers.list();
        return unwrapArray<Customer>(res.data);
      } catch {
        return [] as Customer[];
      }
    },
    retry: false,
  });

  const allInvoices = invoicesData ?? [];
  const invoices = status === 'all' ? allInvoices : allInvoices.filter(inv => inv.status === status);
  const customers = customersData || [];
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = makeToggleSort(setSortBy, setSortDir, sortBy);
  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a: any, b: any) => {
      const aVal = a[sortBy] ?? '';
      const bVal = b[sortBy] ?? '';
      return sortCompare(aVal, bVal, sortDir);
    });
  }, [invoices, sortBy, sortDir]);
  /**
   * Derived from the real list, or null when there is no list to derive from.
   *
   * The sum of no invoices genuinely IS zero, so a tenant with none correctly
   * reads $0.00 -- that is an aggregate, not an absent value. A FAILED request
   * is the different case: nothing is known, and the tiles render an em dash
   * rather than a confident $0.00 the reader would take as a balance.
   */
  const dashboard: InvoiceDashboard | null =
    dashboardData?.outstanding != null
      ? dashboardData
      : invoicesError
        ? null
        : {
            outstanding: invoices.filter(i => i.status === 'sent' || i.status === 'overdue').reduce((s, i) => s + (Number(i.totalAmount) || 0), 0),
            overdueCount: invoices.filter(i => i.status === 'overdue').length,
            paidThisMonth: invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (Number(i.totalAmount) || 0), 0),
            draftsCount: invoices.filter(i => i.status === 'draft').length,
          };

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark">
      <div className="max-w-[1920px] mx-auto">
        

        {/* Title Bar */}
        <div className="border-b border-border px-6 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
            <ArrowLeft size={24} /> Back
          </button>
          <div className="flex items-center justify-between mt-2">
            <div>
              <h1 className="text-base font-bold text-foreground">Invoices</h1>
              <p className="text-sm text-fieldiq-text">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/invoices/generate')}
                className="flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark text-sm font-semibold px-4 py-2 rounded-lg hover:bg-amber-400 transition-colors"
              >
                <Plus size={16} />
                New Invoice
              </button>
            </div>
          </div>
        </div>

        {/* Dashboard Summary Cards */}
        <div className="px-4 py-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Outstanding */}
            <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <DollarSign size={16} className="text-blue-400" />
                </div>
                <span className="text-sm text-fieldiq-muted font-medium">Outstanding</span>
              </div>
              {dashboardLoading ? (
                <Loader2 size={16} className="animate-spin text-fieldiq-muted" />
              ) : (
                <p className="text-lg font-bold text-white">{dashboard ? formatMoney(dashboard.outstanding) : '--'}</p>
              )}
            </div>

            {/* Overdue */}
            <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle size={16} className="text-red-400" />
                </div>
                <span className="text-sm text-fieldiq-muted font-medium">Overdue</span>
              </div>
              {dashboardLoading ? (
                <Loader2 size={16} className="animate-spin text-fieldiq-muted" />
              ) : (
                <p className="text-lg font-bold text-red-400">{dashboard ? dashboard.overdueCount : '--'}</p>
              )}
            </div>

            {/* Paid This Month */}
            <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <CheckCircle size={16} className="text-green-400" />
                </div>
                <span className="text-sm text-fieldiq-muted font-medium">Paid This Month</span>
              </div>
              {dashboardLoading ? (
                <Loader2 size={16} className="animate-spin text-fieldiq-muted" />
              ) : (
                <p className="text-lg font-bold text-green-400">{dashboard ? formatMoney(dashboard.paidThisMonth) : '--'}</p>
              )}
            </div>

            {/* Drafts */}
            <div className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gray-500/10 flex items-center justify-center">
                  <Clock size={16} className="text-gray-400" />
                </div>
                <span className="text-sm text-fieldiq-muted font-medium">Drafts</span>
              </div>
              {dashboardLoading ? (
                <Loader2 size={16} className="animate-spin text-fieldiq-muted" />
              ) : (
                <p className="text-lg font-bold text-white">{dashboard ? dashboard.draftsCount : '--'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="px-4 py-3 border-b border-fieldiq-border">
          <div className="flex items-center gap-3">
            <div className="flex gap-2 overflow-x-auto scrollbar-hide flex-1">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setStatus(tab.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    status === tab.key
                      ? 'bg-fieldiq-accent text-fieldiq-dark'
                      : 'bg-fieldiq-card text-fieldiq-text hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Customer Filter Dropdown */}
            <div className="relative flex items-center gap-2">
              <Filter size={14} className="text-fieldiq-muted" />
              <select
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                className="bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-fieldiq-accent appearance-none pr-8 min-w-[160px]"
              >
                <option value="">All Clients</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice List */}
      <div className="max-w-[1920px] mx-auto px-4 py-4 pb-[var(--bottom-chrome)]">
        {invoicesLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-fieldiq-muted" />
          </div>
        ) : invoicesError ? (
          /* A failed request is not an empty company. Say which one happened. */
          <div className="text-center py-16">
            <AlertTriangle size={40} className="mx-auto text-red-400 mb-3" />
            <p className="text-fieldiq-text text-sm">Couldn't load invoices.</p>
            <p className="text-fieldiq-muted text-xs mt-1">
              This is a problem reaching the server, not a statement about your invoices. Try again in a moment.
            </p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-16">
            <FileText size={40} className="mx-auto text-fieldiq-muted mb-3" />
            <p className="text-fieldiq-text text-sm">
              {allInvoices.length === 0 ? 'No invoices yet.' : 'No invoices match these filters.'}
            </p>
            <p className="text-fieldiq-muted text-xs mt-1">
              {allInvoices.length === 0
                ? 'Invoices you create from approved tickets will appear here.'
                : 'Try a different status or client.'}
            </p>
          </div>
        ) : (
          <>
            {/* ── Desktop Table ── */}
            <div className="hidden md:block bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-fieldiq-border">
                    <SortTh label="Invoice #" field="invoiceNumber" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="w-[130px]" />
                    <SortTh label="Client" field="customerName" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Created" field="createdAt" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Due Date" field="dueDate" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Amount" field="totalAmount" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="!text-right" />
                    <SortTh label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                    <th className="text-xs uppercase tracking-wider font-bold text-fieldiq-muted px-4 py-3 w-[70px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedInvoices.map((invoice) => {
                    const badge = STATUS_BADGE[invoice.status] || STATUS_BADGE.draft;
                    return (
                      <tr
                        key={invoice.id}
                        onClick={() => navigate(`/invoices/${invoice.id}`)}
                        className="cursor-pointer hover:bg-fieldiq-dark/50 transition-colors border-b border-fieldiq-border last:border-b-0"
                      >
                        <td className="px-4 py-2.5 text-sm font-semibold text-fieldiq-accent">
                          {invoice.invoiceNumber || `INV-${invoice.id.slice(0, 8)}`}
                        </td>
                        <td className="px-4 py-2.5 text-sm font-semibold text-white">
                          {invoice.customerName || '—'}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-fieldiq-text">{formatDate(invoice.createdAt, timeZone)}</td>
                        <td className="px-4 py-2.5 text-sm text-fieldiq-text">{formatDay(invoice.dueDate, timeZone)}</td>
                        <td className={`px-4 py-2.5 text-sm font-bold text-right ${
                          invoice.status === 'paid' ? 'text-green-400' : invoice.status === 'overdue' ? 'text-red-400' : 'text-white'
                        }`}>
                          {formatMoney(invoice.totalAmount)}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${STATUS_DOT[invoice.status] || STATUS_DOT.draft}`} />
                            <span className="text-sm font-medium text-white">{badge.label}</span>
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <ChevronRight size={16} className="text-fieldiq-muted" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Mobile Card List ── */}
            <div className="md:hidden space-y-3">
              {sortedInvoices.map((invoice) => {
                const badge = STATUS_BADGE[invoice.status] || STATUS_BADGE.draft;
                return (
                  <div
                    key={invoice.id}
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                    className="bg-fieldiq-card rounded-xl border border-fieldiq-border cursor-pointer hover:border-fieldiq-muted transition-colors"
                  >
                    <div className="px-5 pt-4 pb-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-fieldiq-dark flex items-center justify-center">
                            <FileText size={14} className="text-fieldiq-muted" />
                          </div>
                          <span className="text-sm font-semibold text-white">
                            {invoice.invoiceNumber || `INV-${invoice.id.slice(0, 8)}`}
                          </span>
                        </div>
                        <span
                          className={`text-xs px-2.5 py-1 rounded-full border ${badge.bg} ${badge.textColor} ${badge.border} ${
                            badge.strikethrough ? 'line-through' : ''
                          }`}
                        >
                          {badge.label}
                        </span>
                      </div>
                      <h3 className={`text-base font-bold text-white ${badge.strikethrough ? 'line-through opacity-50' : ''}`}>
                        {invoice.customerName || '—'}
                      </h3>
                    </div>
                    <div className="px-5 py-3 border-t border-fieldiq-border flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-sm text-fieldiq-text">
                        <Clock size={13} />
                        <span>Due {formatDay(invoice.dueDate, timeZone)}</span>
                      </div>
                      <span
                        className={`text-base font-bold ${
                          invoice.status === 'paid'
                            ? 'text-green-400'
                            : invoice.status === 'overdue'
                            ? 'text-red-400'
                            : 'text-fieldiq-accent'
                        }`}
                      >
                        {formatMoney(invoice.totalAmount)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Floating New Invoice Button */}
      <button
        onClick={() => navigate('/invoices/generate')}
        className="fixed bottom-[var(--fab-bottom)] right-5 z-40 w-14 h-14 bg-fieldiq-accent text-fieldiq-dark rounded-full shadow-lg flex items-center justify-center hover:bg-amber-400 transition-colors active:scale-95"
      >
        <Plus size={24} />
      </button>
    </div>
  );
}
